import { useMemo } from 'react';
import { useLocation } from 'react-router';

export function useQueryParams() {
  return new URLSearchParams(useLocation().search);
}

export function useFragmentParams() {
  const searchFragment = useLocation().hash.substring(1);
  // This `useMemo` is not strictly necessary, but when we enabled the React compiler,
  // it caused a regression where the URLSearchParams were being mutated
  // and subsequently reused across renders, leading to unexpected behavior.
  // Using the `useMemo` here makes it clear to readers that we are SHARING the same
  // URLSearchParams instance across renders. If you need to modify the URLSearchParams,
  // you should create a new instance instead of modifying the existing one.
  return useMemo(() => {
    return new URLSearchParams(decodeURIComponent(searchFragment));
  }, [searchFragment]);
}

const urlRegExp = new RegExp(
  '^(https?:\\/\\/)?' + // protocol
    '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
    'localhost|' + // localhost
    '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
    '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
    '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
    '(\\#[-a-z\\d_]*)?$', // fragment locator
  'i',
);

/**
 * Returns a Boolean value that indicates whether or not a string is a valid URL
 * @param value the string to validate
 * @returns true if the string is a valid URL, false otherwise
 */
export const validateUrl = (value: string) => urlRegExp.test(value);

export function goBackOrElse(callbackWhenNoHistory: () => void) {
  const hasHistory: boolean = window.history.state && window.history.state.idx > 0;

  if (hasHistory) {
    window.history.back();
  } else {
    callbackWhenNoHistory();
  }
}

/**
 * Append current URL's search (not hash params) to the target URL
 * @param currentUrl The current URL object containing search and hash parameters
 * @param targetUrl The target URL string to which parameters will be appended
 * @returns A string representation of the target URL with appended parameters
 */
export function appendCurrentParamsToUrl(currentUrl: URL, targetUrl: string): string {
  const target = new URL(targetUrl, currentUrl.origin);

  // Retain existing search params
  currentUrl.searchParams.forEach((value, key) => {
    target.searchParams.set(key, value);
  });

  return target.toString();
}
